/**
 * Feature discovery: every apps/web/src/features/<id>/feature.ts is collected at build time.
 * Validation fails the build or test run loudly instead of silently dropping a feature.
 */
import { NAV_SECTIONS } from "./feature";
import type { FeatureManifest, FeatureNavEntry } from "./feature";

export type FeatureModules = Record<string, { default?: FeatureManifest }>;

export class FeatureRegistryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FeatureRegistryError";
  }
}

export interface NavItem extends FeatureNavEntry {
  featureId: string;
}

const FOLDER = /\/features\/([^/]+)\/feature\.ts$/;

function within(path: string, id: string): boolean {
  return path === `/${id}` || path.startsWith(`/${id}/`);
}

export function collectFeatures(modules: FeatureModules): FeatureManifest[] {
  const byId = new Map<string, FeatureManifest>();
  const paths = new Set<string>();
  for (const [file, module] of Object.entries(modules)) {
    const folder = FOLDER.exec(file)?.[1];
    const manifest = module.default;
    if (!folder || !manifest) {
      throw new FeatureRegistryError(`${file} must default-export defineFeature({...})`);
    }
    if (manifest.id !== folder) {
      throw new FeatureRegistryError(`${file}: id "${manifest.id}" must equal its folder "${folder}"`);
    }
    if (byId.has(manifest.id)) {
      throw new FeatureRegistryError(`feature id "${manifest.id}" is registered twice`);
    }
    if (manifest.routes.length === 0) {
      throw new FeatureRegistryError(`feature "${manifest.id}" declares no routes`);
    }
    for (const route of manifest.routes) {
      if (!within(route.path, manifest.id)) {
        throw new FeatureRegistryError(
          `feature "${manifest.id}": route ${route.path} must live under /${manifest.id}`,
        );
      }
      if (paths.has(route.path)) {
        throw new FeatureRegistryError(`route ${route.path} is declared twice`);
      }
      paths.add(route.path);
    }
    if (manifest.nav && !manifest.routes.some((route) => route.path === manifest.nav?.path)) {
      throw new FeatureRegistryError(
        `feature "${manifest.id}": nav path ${manifest.nav.path} is not one of its routes`,
      );
    }
    byId.set(manifest.id, manifest);
  }
  return [...byId.values()].sort((a, b) => a.id.localeCompare(b.id));
}

export function navItems(
  features: FeatureManifest[],
  allowed: (permissions: string[] | undefined) => boolean,
): NavItem[] {
  return features
    .filter((feature) => feature.nav && allowed(feature.permissions))
    .map((feature) => ({ ...(feature.nav as FeatureNavEntry), featureId: feature.id }))
    .sort(
      (a, b) =>
        NAV_SECTIONS.indexOf(a.section) - NAV_SECTIONS.indexOf(b.section) ||
        (a.order ?? 100) - (b.order ?? 100) ||
        a.label.localeCompare(b.label),
    );
}

export const features: FeatureManifest[] = collectFeatures(
  import.meta.glob<{ default: FeatureManifest }>("../features/*/feature.ts", { eager: true }),
);
